<script lang="ts">
  import type { Api } from "../../lib/api";
  import { ApiError } from "../../lib/api";
  import Card from "../../lib/ui/Card.svelte";
  import Pill from "../../lib/ui/Pill.svelte";
  import Banner from "../../lib/ui/Banner.svelte";
  import {
    Button,
    Input,
    Label,
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    dialogs,
  } from "@price-check/ui";

  let {
    api,
    orgId,
    plan,
    trialEndsAt,
    onSaved,
  }: {
    api: Api;
    orgId: string;
    plan?: string;
    trialEndsAt?: string | null;
    onSaved: () => void;
  } = $props();

  const PLANS = ["trial", "paid"] as const;

  const currentPlan = $derived(plan === "paid" ? "paid" : "trial");
  const expired = $derived(
    currentPlan === "trial" && !!trialEndsAt && Date.parse(trialEndsAt) <= Date.now(),
  );

  let editPlan = $state<"trial" | "paid">("trial");
  let editDate = $state("");
  let error = $state("");
  let saving = $state(false);

  $effect(() => {
    editPlan = currentPlan;
    editDate = trialEndsAt ? trialEndsAt.slice(0, 10) : "";
  });

  const dirty = $derived(
    editPlan !== currentPlan || editDate !== (trialEndsAt ? trialEndsAt.slice(0, 10) : ""),
  );

  async function save() {
    if (saving || !dirty) return;
    if (currentPlan === "paid" && editPlan === "trial") {
      const ok = await dialogs.confirm({
        title: "Downgrade to trial",
        message:
          "Downgrading to trial can lock this customer out of bulk RFQ quoting once the trial end passes. Continue?",
        confirmLabel: "Downgrade",
        tone: "danger",
      });
      if (!ok) return;
    }
    error = "";
    saving = true;
    try {
      await api.setOrgPlan(orgId, {
        plan: editPlan,
        trialEndsAt: editPlan === "trial" && editDate ? new Date(editDate).toISOString() : null,
      });
      onSaved();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "could not save the plan";
    } finally {
      saving = false;
    }
  }
</script>

<Card title="Plan" subtitle="Bulk RFQ quoting + CSV export are paid; a trial has full access until its end date.">
  <div class="flex flex-wrap items-center gap-3">
    {#if currentPlan === "paid"}
      <Pill tone="ok">paid</Pill>
    {:else if expired}
      <Pill tone="amber">trial expired</Pill>
    {:else}
      <Pill tone="slate">trial</Pill>
    {/if}
    {#if currentPlan === "trial" && trialEndsAt}
      <span class="text-sm text-muted">
        Trial ends {new Date(trialEndsAt).toLocaleDateString()}
      </span>
    {:else if currentPlan === "trial"}
      <span class="text-sm text-muted">Unlimited trial (no end date set)</span>
    {/if}
  </div>

  <div class="mt-3 flex flex-wrap items-center gap-2.5">
    <Select type="single" value={editPlan} onValueChange={(v) => v && (editPlan = v as "trial" | "paid")}>
      <SelectTrigger size="sm" aria-label="Plan">{editPlan}</SelectTrigger>
      <SelectContent>
        {#each PLANS as p (p)}<SelectItem value={p} label={p} />{/each}
      </SelectContent>
    </Select>
    {#if editPlan === "trial"}
      <Label class="flex items-center gap-1.5 text-sm font-normal">
        Trial ends
        <Input class="w-40" type="date" aria-label="Trial end date" bind:value={editDate} />
      </Label>
    {/if}
    <Button size="sm" disabled={saving || !dirty} onclick={save}>
      {saving ? "Saving…" : "Save plan"}
    </Button>
  </div>

  {#if error}<div class="mt-3"><Banner tone="danger">{error}</Banner></div>{/if}
</Card>
